import { apiClient } from '../apiClient';
import { httpGet } from '../http/httpClient';
import type { ApiResponse } from '../http/types';
import type {
  Attachment,
  AttachmentDto,
  AttachmentGroupDto,
  ListAttachmentsParams,
  UploadAttachmentRequest,
  CreateAttachmentGroupPayload,
  UpdateAttachmentGroupPayload,
  PagedResult,
} from './attachment.types';

const API_BASE = '/api/attachment';

export function listAttachments(params: ListAttachmentsParams = {}): Promise<ApiResponse<PagedResult<Attachment>>> {
  return apiClient.get(`${API_BASE}` as any, { params } as any);
}

export function getAttachment(id: string | number): Promise<ApiResponse<AttachmentDto>> {
  return apiClient.get<AttachmentDto>(`${API_BASE}/${id}` as any);
}

export function deleteAttachment(id: string | number): Promise<ApiResponse<void>> {
  return apiClient.delete<void>(`${API_BASE}/${id}` as any);
}

export async function downloadAttachment(id: string | number, fileName: string): Promise<void> {
  try {
    // Prefer the new route: GET /api/attachment/{id}/download
    // Fallback to the older /api/attachment/download/{id} if the new route fails.
    let response = await httpGet<Blob>(`${API_BASE}/${id}/download`, {
      responseType: 'blob',
    });
    if (!response.success) {
      // try the legacy path
      response = await httpGet<Blob>(`${API_BASE}/download/${id}`, {
        responseType: 'blob',
      });
    }
    
    if (!response.success || !response.data) {
      throw new Error(response.error?.message || 'Download failed');
    }
    
    // Create a blob URL and trigger download
    const blob = response.data;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  } catch (err) {
    console.info('Download failed:', err);
    throw new Error('Network error - please check your connection');
  }
}

export function uploadAttachment(req: UploadAttachmentRequest): Promise<ApiResponse<any>> {
  const form = new FormData();
  const fileName = req.fileName || (req.file as File)?.name || 'upload.bin';
  
  // Add file first
  form.append('file', req.file, fileName);
  if (req.originalFileName) {
    form.append('originalFileName', req.originalFileName);
  }
  
  // Add required groupId or alarmId
  if (req.groupId) {
    form.append('groupId', String(req.groupId));
  }
  if (req.alarmId) {
    form.append('alarmId', String(req.alarmId));
  }
  if (req.description) {
    form.append('description', req.description);
  }
  
  for (const [key, value] of form.entries()) {
    console.info(`  ${key}:`, value instanceof File ? `File(${value.name}, ${value.size} bytes)` : value);
  }
  
  // POST to /api/attachment/upload
  return apiClient.post(`${API_BASE}/upload` as any, form as any, { headers: { 'Content-Type': 'multipart/form-data' } } as any);
}

export function listAttachmentGroups(): Promise<ApiResponse<AttachmentGroupDto[]>> {
  return apiClient.get<AttachmentGroupDto[]>(`${API_BASE}/groups` as any);
}

export function createAttachmentGroup(payload: CreateAttachmentGroupPayload): Promise<ApiResponse<AttachmentGroupDto>> {
  return apiClient.post<AttachmentGroupDto>(`${API_BASE}/groups` as any, payload as any);
}

export function updateAttachmentGroup(id: number, payload: UpdateAttachmentGroupPayload): Promise<ApiResponse<AttachmentGroupDto>> {
  return apiClient.put<AttachmentGroupDto>(`${API_BASE}/groups/${id}` as any, payload as any);
}

export function deleteAttachmentGroup(id: number): Promise<ApiResponse<void>> {
  const url = `${API_BASE}/groups/${id}`;
  return apiClient.delete<void>(url as any);
}

export async function getAttachmentsByGroup(groupId: number): Promise<ApiResponse<AttachmentDto[]>> {
  const response = await apiClient.get<AttachmentDto[]>(`${API_BASE}/groups/${groupId}/attachments`);
  // This endpoint uses 404 for an existing group with no attachments.
  if (!response.success && response.error?.status === 404 &&
      response.error.message === `No attachments found for group ID ${groupId}`) {
    return { success: true, data: [] };
  }
  return response;
}
